import type { IntakeSpec, MechanicalFastenerPartDetails } from '@shared/types';
import type { Part, Joint, Feature } from '@shared/spec';

function getDetails(intake: IntakeSpec): MechanicalFastenerPartDetails {
  return intake.details as MechanicalFastenerPartDetails;
}

const EXOTIC_TYPES = new Set(['nut', 'washer', 'stud', 'rivet'] as const);
type ExoticType = 'nut' | 'washer' | 'stud' | 'rivet';

export function parts(intake: IntakeSpec): Part[] {
  const d = getDetails(intake);

  if (EXOTIC_TYPES.has(d.fastenerType as ExoticType)) {
    // Exotic types (nut, washer, stud, rivet): not directly representable in
    // FastenerPart kind — emit a custom placeholder. Extended fastener types
    // are deferred to B2 rest.
    return [
      {
        id: 'fastener_part',
        name: `${d.fastenerType} ${d.size}`,
        kind: 'custom',
        materialId: 'mat-primary',
        openscadSnippet: `// ${d.fastenerType} ${d.size} placeholder`,
        boundingBox: { w: 20, d: 20, h: d.length },
        position: [0, 0, 0],
        rotation: [0, 0, 0],
        tags: ['fastener'],
        paramRefs: [],
      },
    ];
  }

  // Map head: 'countersunk' → 'flat'
  let mappedHead: 'none' | 'flat' | 'socket' | 'hex' | 'button' | 'pan' | 'cheese' | 'countersunk' = d.head;
  if (d.head === 'countersunk') {
    // FastenerPart head 'countersunk' is not in the schema enum yet; map to 'flat'.
    // Extended fastener head types are deferred to B2 rest.
    mappedHead = 'flat';
  }

  const validHead = mappedHead as 'none' | 'flat' | 'socket' | 'hex' | 'button' | 'pan' | 'cheese';

  const fastenerType = d.fastenerType as 'screw' | 'bolt' | 'dowel' | 'nail';

  return [
    {
      id: 'fastener',
      name: `${d.fastenerType} ${d.size}`,
      kind: 'fastener',
      materialId: 'mat-primary',
      fastenerType,
      size: d.size,
      length: d.length,
      head: validHead,
      quantity: 1,
      position: [0, 0, 0],
      rotation: [0, 0, 0],
      tags: ['fastener'],
      paramRefs: [],
    },
  ];
}

export function joints(_intake: IntakeSpec): Joint[] {
  return [];
}

export function features(_intake: IntakeSpec): Feature[] {
  return [];
}
